import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
  DestroyRef,
  NgZone,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MeterRole, PhaseCount } from '@core/models';
import { ConfigurationComponent } from '@custom-components/sections/insights/configuration/configuration.component';
import { HeatpumpComponent } from '../insights/heatpump/heatpump.component';
import { EmeterComponent } from '../insights/emeter/emeter.component';
import { ElectricalComponent } from '../insights/electrical/electrical.component';
import { ChartBuilder } from '../insights/chartBuilder';
import { FALLBACK_TIME_ZONE, zoneLabels } from '@core/helpers/display-time.helper';
import { hasReadings, phaseIndices, readings } from '../insights/phaseUtils';
import { TopologicalDeviceTelemetryStore } from '@root/app/store/topological-device-telemetry.store';
import { ChartSyncService } from '@custom-components/general/chart/chart-sync.service';
import { CardComponent } from '@custom-components/general/card';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { TableComponent } from '@custom-components/general/table/table.component';
import { NoItemsFoundComponent } from '@custom-components/general/no-items-found/no-items-found.component';
import { TableColumnConfig } from '@core/models/skeleton.model';
import { ChartRangeOptions } from '@core/models';
import {
  EnergyFlowBatteryIntervalModel,
  EnergyFlowEmeterIntervalModel,
  EnergyFlowEvChargerIntervalModel,
  EnergyFlowHeatpumpIntervalModel,
  EnergyFlowIntervalModel,
  EnergyFlowPvForecastIntervalModel,
  EnergyFlowSolarIntervalModel,
} from '@core/models/topological-telemetry.model';
import { PerformanceRatioComponent } from '../insights/performance-ratio/performance-ratio.component';
import { anchorDayFor } from '../insights/performance-ratio/performance-anchor';
import {
  pvPerformanceRatios,
  pvPerformanceTrend,
} from '../insights/performance-ratio/performance-ratio.mapper';
import { DeviceStore } from '@root/app/store/device.store';
import { PvPerformanceStore } from '@root/app/store/pv-performance.store';

// A presentational table row: the period label plus one formatted value per column.
type DisplayRow = Record<string, unknown>;

// One column of the combined data table: a stable field key, a fully-resolved header
// (label + unit), and a formatter turning an interval into a display string.
interface DataColumn {
  field: string;
  label: string;
  weight?: number;
  value: (interval: EnergyFlowIntervalModel) => string;
}

// A device metric: a translation key for its name, its display unit (shown in the
// column header, empty for unitless values like COP), decimals, and a raw extractor
// already converted to the display unit (e.g. Wh → kWh).
interface MetricSpec<T> {
  label: string;
  unit: string;
  decimals: number;
  raw: (item: T) => number | null;
}

// Relative column widths, resolved to percentages once the column set is known. Percentages
// (not rem/px) because under table-layout:fixed any width left over after absolute widths is
// handed back to *every* column, which flattens the difference to a few pixels.
const COLUMN_WEIGHT = {
  // Bare short numbers: "230", "-1.23".
  electrical: 1,
  // Wide enough for a full weekday name ("Wednesday") in the week view.
  period: 1.5,
  // Long headers, e.g. "Grid meter reading delivered (kWh)".
  default: 2,
} as const;

const toKwh = (wh: number | null | undefined): number | null => (wh == null ? null : wh / 1000);

// Last segment of a translation key, used as a short, stable column field id.
const slug = (key: string): string => key.split('.').pop() ?? key;

// Format a number for a cell, or an en-dash when absent. Units live in the header, so
// cells stay bare numbers.
function formatNumber(value: number | null | undefined, decimals: number): string {
  return value == null ? '–' : value.toFixed(decimals);
}

// Card-header energy totals: one decimal while it still carries information, none once the
// number runs into the hundreds ("12.4 kWh", "352 kWh").
function formatKwh(kwh: number): string {
  const decimals = Math.abs(kwh) >= 100 ? 0 : 1;
  const rounded = Number(kwh.toFixed(decimals));
  // A tiny negative reading (and JS's -0) would otherwise render as "-0.0 kWh".
  return `${(rounded === 0 ? 0 : rounded).toFixed(decimals)} kWh`;
}

const EMETER_ENERGY_METRICS: MetricSpec<EnergyFlowEmeterIntervalModel>[] = [
  {
    label: 'devices.detailTabs.insights.consumed',
    unit: 'kWh',
    decimals: 2,
    raw: e => toKwh(e.importedInWh),
  },
  {
    label: 'devices.detailTabs.insights.delivered',
    unit: 'kWh',
    decimals: 2,
    raw: e => toKwh(e.exportedInWh),
  },
  // Cumulative register readings: the meter's own totalising counters, not the
  // per-interval deltas charted above.
  {
    label: 'devices.detailTabs.insights.meterReadingConsumed',
    unit: 'kWh',
    decimals: 2,
    raw: e => toKwh(e.importedInWhCumulativeValue),
  },
  {
    label: 'devices.detailTabs.insights.meterReadingDelivered',
    unit: 'kWh',
    decimals: 2,
    raw: e => toKwh(e.exportedInWhCumulativeValue),
  },
];

const SOLAR_METRICS: MetricSpec<EnergyFlowSolarIntervalModel>[] = [
  {
    label: 'devices.detailTabs.insights.solar.generated',
    unit: 'kWh',
    decimals: 2,
    raw: s => toKwh(s.generatedInWh),
  },
];

// Solar generation forecast (clear-sky theoretical max + weather-adjusted).
const PV_FORECAST_METRICS: MetricSpec<EnergyFlowPvForecastIntervalModel>[] = [
  {
    label: 'devices.detailTabs.insights.solar.forecastClearSky',
    unit: 'kWh',
    decimals: 2,
    raw: p => toKwh(p.clearSkyInWh),
  },
  {
    label: 'devices.detailTabs.insights.solar.forecastWeather',
    unit: 'kWh',
    decimals: 2,
    raw: p => toKwh(p.weatherInWh),
  },
];

const BATTERY_METRICS: MetricSpec<EnergyFlowBatteryIntervalModel>[] = [
  {
    label: 'devices.detailTabs.insights.battery.charged',
    unit: 'kWh',
    decimals: 2,
    raw: b => toKwh(b.chargedInWh),
  },
  {
    label: 'devices.detailTabs.insights.battery.discharged',
    unit: 'kWh',
    decimals: 2,
    raw: b => toKwh(b.dischargedInWh),
  },
  {
    label: 'devices.detailTabs.insights.battery.soc',
    unit: '%',
    decimals: 0,
    raw: b => b.stateOfCharge,
  },
];

const EVCHARGER_METRICS: MetricSpec<EnergyFlowEvChargerIntervalModel>[] = [
  {
    label: 'devices.detailTabs.insights.evcharger.charged',
    unit: 'kWh',
    decimals: 2,
    raw: e => toKwh(e.chargedInWh),
  },
];

const HEATPUMP_ENERGY_METRICS: MetricSpec<EnergyFlowHeatpumpIntervalModel>[] = [
  {
    label: 'devices.detailTabs.insights.heatpump.consumed',
    unit: 'kWh',
    decimals: 2,
    raw: h => toKwh(h.energyUsedInWh),
  },
  {
    label: 'devices.detailTabs.insights.heatpump.thermal_energy',
    unit: 'kWh',
    decimals: 2,
    raw: h => toKwh(h.thermalEnergyInWh),
  },
  {
    label: 'devices.detailTabs.insights.heatpump.cop',
    unit: '',
    decimals: 2,
    raw: h => h.coefficientOfPerformance,
  },
];

const HEATPUMP_TEMPERATURE_METRICS: MetricSpec<EnergyFlowHeatpumpIntervalModel>[] = [
  {
    label: 'devices.detailTabs.insights.heatpump.boiler_target_temperature',
    unit: '°C',
    decimals: 1,
    raw: h => h.boilerTargetTemperature,
  },
  {
    label: 'devices.detailTabs.insights.heatpump.domestic_hot_water_temperature',
    unit: '°C',
    decimals: 1,
    raw: h => h.domesticHotWaterTemperature,
  },
  // Stored as m³/s; shown in litres/minute (×1000 ×60).
  {
    label: 'devices.detailTabs.insights.heatpump.flow_rate',
    unit: 'l/min',
    decimals: 2,
    raw: h => (h.flowRate == null ? null : h.flowRate * 1000 * 60),
  },
  {
    label: 'devices.detailTabs.insights.heatpump.leaving_water_temp_pre_backup_heater',
    unit: '°C',
    decimals: 1,
    raw: h => h.leavingWaterTempPreBackupHeater,
  },
  {
    label: 'devices.detailTabs.insights.heatpump.room_temperature',
    unit: '°C',
    decimals: 1,
    raw: h => h.roomTemperature,
  },
  {
    label: 'devices.detailTabs.insights.heatpump.outside_air_temperature',
    unit: '°C',
    decimals: 1,
    raw: h => h.outsideAirTemperature,
  },
  {
    label: 'devices.detailTabs.insights.heatpump.return_water_temperature',
    unit: '°C',
    decimals: 1,
    raw: h => h.returnWaterTemperature,
  },
  {
    label: 'devices.detailTabs.insights.heatpump.thermostat_target_temperature',
    unit: '°C',
    decimals: 1,
    raw: h => h.thermostatTargetTemperature,
  },
];

// Channel labels are type + channel number ("grid", "grid_0", "emeter_0", "pv_1"), so the
// netaansluiting is recognisable by that exact shape. A looser prefix match would also claim a
// genuine subchannel like "Grid-tied inverter", and since the grid connection wins over every
// other channel in the totals below, that one channel would then silently swallow the rest.
const GRID_EMETER_LABEL = /^grid(_\d+)?$/;

// One emeter contributing readings: the grid connection or a labelled channel.
interface MeterSource {
  id: string;
  label: string;
  get: (interval: EnergyFlowIntervalModel) => EnergyFlowEmeterIntervalModel | undefined;
}

@Component({
  selector: 'xemex-topological-insights-content',
  // Scopes a chart hover-sync group to this page so hovering one chart mirrors the
  // tooltip on every other chart at the same interval.
  providers: [ChartSyncService],
  imports: [
    TranslateModule,
    ConfigurationComponent,
    HeatpumpComponent,
    EmeterComponent,
    ElectricalComponent,
    CardComponent,
    CardGridComponent,
    TableComponent,
    NoItemsFoundComponent,
    PerformanceRatioComponent,
  ],
  templateUrl: './insights-topological-content.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './insights-topological-content.component.scss',
})
export class InsightsTopologicalContentComponent {
  telemetryStore = inject(TopologicalDeviceTelemetryStore);
  pvPerformanceStore = inject(PvPerformanceStore);
  private deviceStore = inject(DeviceStore);
  private translate = inject(TranslateService);

  // RDT-589: forwarded to xemex-electrical-insights and used to clamp the combined data
  // table's per-phase columns below. See electrical.component.ts for the clamp semantics.
  phaseCountOverride = input<PhaseCount | undefined>();
  // Threaded down to xemex-emeter-insights/xemex-electrical-insights; sign-flip behavior
  // for 'production' lands in RDT-592 portal-T5/T6, this is plumbing only.
  meterRole = input<MeterRole>('bidirectional');
  // The device is visible but its telemetry is not shared with the user's organisation.
  telemetryNotShared = input(false);

  readonly gridContext = { Grid: true } as const;
  readonly energyContext = {
    Warmtepomp: true,
    SolarPanel: true,
    Battery: true,
    EVCharger: true,
    Emeters: true,
  } as const;

  private readonly _availableContexts = computed(() => {
    const intervals = this.getIntervals();
    return {
      Warmtepomp: intervals.some(i => i.heatPump?.length > 0),
      SolarPanel: intervals.some(i => i.solarPanels?.length > 0 || i.pv?.length > 0),
      Battery: intervals.some(i => i.battery?.length > 0),
      EVCharger: intervals.some(i => i.evCharger?.length > 0),
      Emeters: intervals.some(i => i.emeters?.length > 0),
    };
  });

  hasEnergyContext = computed(() => Object.values(this._availableContexts()).some(Boolean));

  hasGridContext = computed(() =>
    this.getIntervals().some(i => i.electricityGrid && i.electricityGrid.quality !== 'missing'),
  );

  hasPowerData = computed(() => this.hasElectricalMetric('activePowerInW'));
  hasVoltageData = computed(() => this.hasElectricalMetric('voltageInV'));

  // The performance card judges photovoltaic yield, so it needs both: a meter whose role is
  // to measure production, and telemetry that actually reports solar production or a
  // production forecast. A bidirectional meter behind panels reads yield too, but the ratio
  // is only meaningful against a meter that sees nothing else.
  //
  // Once both hold, the card behaves unlike every other card on this page: it stays visible
  // even when its own numbers are missing, because the verdict chip reads "not enough data",
  // which is something a reader needs to know. It is deliberately left out of hasNoData()
  // below — a card that never disappears would otherwise keep the page-wide empty state from
  // ever firing.
  // The ratio document is the device's own answer to "is there PV yield here", and unlike the
  // interval channels it does not depend on the selected period: a year selection aggregates the
  // solar channel away, which used to make the card disappear on a device it had just been shown
  // for. Scoped to the current device, because the store is root-provided.
  // The store is root-provided, so the previous device's document survives the navigation and
  // nothing orders its reset against the telemetry that reveals the card. Every read goes through
  // this guard, so the card can never show one device's ratios under another device's name.
  private readonly loadedPerformance = computed(() => {
    const deviceId = this.deviceStore.deviceDetail()?.deviceId;
    if (!deviceId || this.pvPerformanceStore.deviceId() !== deviceId) return null;
    return this.pvPerformanceStore.performance();
  });

  // Mere presence of a document proves nothing: the endpoint answers 200 with every ratio null for
  // a production meter that has no panels at all, so only a measured ratio - in a window or
  // anywhere on the trend - tells the two apart.
  private readonly hasMeasuredRatio = computed(() => {
    const document = this.loadedPerformance();
    if (!document) return false;
    return (
      Object.values(document.windows).some(window => window.ratio !== null) ||
      document.trend7d.some(point => point.ratio !== null)
    );
  });

  hasPerformanceContext = computed(
    () =>
      this.meterRole() === 'production' &&
      (this._availableContexts().SolarPanel || this.hasMeasuredRatio()),
  );

  // Today, as far as this card is concerned. A `new Date()` read straight from the computed
  // below would be memoized with it, so a session left open across midnight would keep
  // anchoring on the day before yesterday. The clock is re-read on the only event that can
  // change the answer - the day rolling over - which is a single timeout re-armed once a day,
  // not a poll.
  private readonly zone = inject(NgZone);
  private readonly today = signal(new Date());
  private rolloverTimer: ReturnType<typeof setTimeout> | undefined;

  performanceAnchorDate = computed(() =>
    anchorDayFor(this.telemetryStore.selectedDate(), this.today()),
  );

  performanceRatios = computed(() => pvPerformanceRatios(this.loadedPerformance()));
  performanceTrend = computed(() => pvPerformanceTrend(this.loadedPerformance()));

  // Every time on this page is on the installation's clock. When that zone is unknown the fallback
  // zone stands in, and the page says so rather than passing it off as the installation's.
  timeZoneNote = computed(() => {
    const zone = this.telemetryStore.timeZone?.() ?? FALLBACK_TIME_ZONE;
    const fallback = this.telemetryStore.timeZoneIsFallback?.() ?? true;
    return this.translate.instant(
      fallback
        ? 'devices.detailTabs.insights.timeZoneFallback'
        : 'devices.detailTabs.insights.timeZone',
      { zone, labels: zoneLabels(zone, this.today().getFullYear()) },
    );
  });

  constructor() {
    // Keyed on the anchor day rather than on the raw selected period, so this re-fires only on a
    // change that actually moves the day. Guarded on the meter role, not on card visibility:
    // visibility partly depends on this very response. Telemetry that is not shared would only
    // be refused again by the ratio route.
    effect(() => {
      const deviceId = this.deviceStore.deviceDetail()?.deviceId;
      const day = this.performanceAnchorDate();
      if (!deviceId || day === null || this.meterRole() !== 'production') return;
      if (this.telemetryNotShared()) return;
      this.pvPerformanceStore.getPvPerformance({ deviceId, day });
    });

    this.scheduleDayRollover();
    inject(DestroyRef).onDestroy(() => clearTimeout(this.rolloverTimer));
  }

  // Scheduled outside the Angular zone on purpose. A timer armed for the next midnight is a
  // pending macrotask up to 24 hours out, and inside the zone that means the zone never goes
  // stable - every test that waits for stability then hangs until Karma drops the browser.
  // Writing the signal is enough to drive change detection; the zone is not needed for that.
  private scheduleDayRollover(): void {
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    this.zone.runOutsideAngular(() => {
      this.rolloverTimer = setTimeout(() => {
        this.today.set(new Date());
        this.scheduleDayRollover();
      }, nextMidnight.getTime() - now.getTime());
    });
  }

  /**
   * Period import/export totals for the Energy card header.
   *
   * Which meters to add up is not obvious: emeter subchannels normally sit *behind* the grid
   * connection, so adding both would count the same energy twice. The grid connection is the
   * installation's boundary, so it wins whenever it reports; a device that only has
   * subchannels (a standalone production CT clamp, for instance) falls back to summing those.
   *
   * Export follows the sign convention of the charts on this page — negative, because the
   * energy leaves the installation, except on a production-role meter whose own output *is*
   * the exported reading and so reads positive. The data table uses unsigned values; matching
   * the chart rather than the table keeps the number next to a chart consistent with it.
   *
   * A production-only meter has no import to report, so that total is omitted entirely rather
   * than shown as a zero - and symmetrically, a consumption-only meter has no export to report.
   *
   * The netaansluiting is not always mirrored in `electricityGrid`: some devices report it as a
   * regular emeter channel instead (labelled e.g. "grid_0"), with `electricityGrid` left at
   * quality 'missing'. Either shape counts as the netaansluiting so it still wins over genuine
   * subchannels.
   */
  energyTotals = computed<{ imported: string | null; exported: string | null } | null>(() => {
    const intervals = this.getIntervals();
    if (!intervals.length) return null;

    const isGridEmeter = (e: EnergyFlowEmeterIntervalModel): boolean =>
      GRID_EMETER_LABEL.test((e.label ?? '').trim().toLowerCase());

    const gridMeter = (
      interval: EnergyFlowIntervalModel,
    ): EnergyFlowEmeterIntervalModel | undefined => {
      const dedicated = interval.electricityGrid;
      if (dedicated && dedicated.quality !== 'missing') return dedicated;
      return (interval.emeters ?? []).find(e => isGridEmeter(e) && e.quality !== 'missing');
    };

    const useGrid = intervals.some(i => gridMeter(i) != null);
    const meters = (interval: EnergyFlowIntervalModel): EnergyFlowEmeterIntervalModel[] => {
      if (useGrid) {
        const grid = gridMeter(interval);
        return grid ? [grid] : [];
      }
      return (interval.emeters ?? []).filter(e => e.quality !== 'missing');
    };

    const sum = (field: 'importedInWh' | 'exportedInWh'): number | null => {
      let total: number | null = null;
      for (const interval of intervals) {
        for (const meter of meters(interval)) {
          const value = meter[field];
          if (value != null) total = (total ?? 0) + value;
        }
      }
      return total;
    };

    const imported = this.meterRole() === 'production' ? null : sum('importedInWh');
    const exported = this.meterRole() === 'consumption' ? null : sum('exportedInWh');
    if (imported === null && exported === null) return null;

    const exportSign = this.meterRole() === 'production' ? 1 : -1;

    return {
      imported: imported === null ? null : formatKwh(imported / 1000),
      exported: exported === null ? null : formatKwh((exportSign * exported) / 1000),
    };
  });

  showTelemetryNotShared = computed(
    () => this.telemetryNotShared() && !this.telemetryStore.isLoadingDeviceTelemetry(),
  );

  hasNoData = computed(
    () =>
      !this.telemetryStore.isLoadingDeviceTelemetry() &&
      this.currentTelemetryData() !== null &&
      !this.hasGridContext() &&
      !this.hasEnergyContext() &&
      !this.hasPowerData() &&
      !this.hasVoltageData() &&
      !this.hasDataTable(),
  );

  // True when any meter (grid or a labelled channel) reports non-empty readings for
  // the given per-phase field in the selected period.
  private hasElectricalMetric(field: keyof EnergyFlowEmeterIntervalModel): boolean {
    return this.getIntervals().some(
      i =>
        hasReadings(i.electricityGrid, field) || (i.emeters ?? []).some(e => hasReadings(e, field)),
    );
  }

  private currentTelemetryData = computed(() => {
    switch (this.telemetryStore.deviceChartTimeOption()) {
      case ChartRangeOptions.DayPerQuarter:
        return this.telemetryStore.deviceTopologicalTelemetryDayPerQuarterHourData();
      case ChartRangeOptions.Day:
        return this.telemetryStore.deviceTopologicalTelemetryDayPerHourData();
      case ChartRangeOptions.Week:
        return this.telemetryStore.deviceTopologicalTelemetryWeekPerDayData();
      case ChartRangeOptions.Month:
        return this.telemetryStore.deviceTopologicalTelemetryMonthPerDayData();
      default:
        return this.telemetryStore.deviceTopologicalTelemetryYearPerMonthData();
    }
  });

  heatpumpChannels = computed(() => {
    const intervals = this.getIntervals();
    const seen = new Map<number, string>();
    for (const interval of intervals) {
      for (const hp of interval.heatPump ?? []) {
        if (!seen.has(hp.channelNumber)) {
          seen.set(hp.channelNumber, hp.label ?? `Heatpump ${hp.channelNumber}`);
        }
      }
    }
    return Array.from(seen.entries()).map(([channelNumber, label]) => ({ channelNumber, label }));
  });

  // Header for the leading time column, matching the active granularity.
  private periodHeader = computed(() => {
    switch (this.telemetryStore.deviceChartTimeOption()) {
      case ChartRangeOptions.DayPerQuarter:
      case ChartRangeOptions.Day:
        return 'general.time';
      case ChartRangeOptions.Week:
      case ChartRangeOptions.Month:
        return 'general.day';
      default:
        return 'general.month';
    }
  });

  // The single combined data table: one row per interval, columns spanning power,
  // voltage, and every available energy/telemetry series. Columns with no data in the
  // selected period are dropped, so a device only shows what it actually reports.
  dataTable = computed<{ columns: TableColumnConfig[]; rows: DisplayRow[] }>(() => {
    if (this.telemetryStore.isLoadingDeviceTelemetry()) return { columns: [], rows: [] };

    const builder = new ChartBuilder(this.telemetryStore, this.translate).withChartSetup();
    const intervals = builder.chartIntervals;
    if (!intervals.length) return { columns: [], rows: [] };
    // Day/quarter labels are strings ("14:00"); month/year labels are numbers. Coerce
    // to string and strip the ​ quarter-hour marker.
    const labels = builder.intervalLabels.map(l => String(l).replace('​', ''));

    const dataColumns = [
      ...this.perPhaseColumns(intervals, 'activePowerInW', 'P', 'kW', 1 / 1000, 2, true),
      ...this.perPhaseColumns(intervals, 'voltageInV', 'V L', 'V', 1, 0, false),
      ...this.meterEnergyColumns(intervals),
      ...this.deviceColumns(intervals, i => i.solarPanels, SOLAR_METRICS, 'solar'),
      ...this.deviceColumns(intervals, i => i.pv, PV_FORECAST_METRICS, 'pv'),
      ...this.deviceColumns(intervals, i => i.battery, BATTERY_METRICS, 'battery'),
      ...this.deviceColumns(intervals, i => i.evCharger, EVCHARGER_METRICS, 'ev'),
      ...this.deviceColumns(intervals, i => i.heatPump, HEATPUMP_ENERGY_METRICS, 'hp-energy'),
      ...this.deviceColumns(intervals, i => i.heatPump, HEATPUMP_TEMPERATURE_METRICS, 'hp-temp'),
    ].filter(col => intervals.some(iv => col.value(iv) !== '–'));

    if (!dataColumns.length) return { columns: [], rows: [] };

    const weights = [
      COLUMN_WEIGHT.period,
      ...dataColumns.map(c => c.weight ?? COLUMN_WEIGHT.default),
    ];
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    const widthOf = (index: number) => `${((weights[index] / totalWeight) * 100).toFixed(4)}%`;

    const columns: TableColumnConfig[] = [
      { field: 'period', label: this.periodHeader(), width: widthOf(0) },
      ...dataColumns.map((c, i) => ({ field: c.field, label: c.label, width: widthOf(i + 1) })),
    ];
    const rows: DisplayRow[] = intervals.map((interval, index) => {
      const row: DisplayRow = { period: labels[index] ?? String(this.periodValue(interval)) };
      for (const col of dataColumns) row[col.field] = col.value(interval);
      return row;
    });

    return { columns, rows };
  });

  hasDataTable = computed(() => this.dataTable().columns.length > 0);

  private getIntervals(): EnergyFlowIntervalModel[] {
    return this.currentTelemetryData()?.intervals ?? [];
  }

  // Fallback x-axis value when a formatted chart label is unavailable.
  private periodValue(interval: EnergyFlowIntervalModel): number {
    const i = interval as unknown as Record<string, number | undefined>;
    return i['hour'] ?? i['quarter_hour'] ?? i['day'] ?? i['month'] ?? 0;
  }

  // ---- Per-phase electrical columns (power, voltage) ----

  private perPhaseColumns(
    intervals: EnergyFlowIntervalModel[],
    field: keyof EnergyFlowEmeterIntervalModel,
    phasePrefix: string,
    unit: string,
    scale: number,
    decimals: number,
    withTotal: boolean,
  ): DataColumn[] {
    const sources = this.meterSources(intervals, field);
    const multi = sources.length > 1;
    const columns: DataColumn[] = [];

    for (const source of sources) {
      const indices = phaseIndices(intervals, source.get, field, this.phaseCountOverride());
      const namePrefix = multi ? `${source.label} ` : '';

      for (const phase of indices) {
        columns.push({
          field: `${source.id}-${field}-l${phase + 1}`,
          label: `${namePrefix}${phasePrefix}${phase + 1} (${unit})`,
          weight: COLUMN_WEIGHT.electrical,
          value: interval => {
            const reading = readings(source.get(interval), field)?.[phase];
            return reading == null ? '–' : formatNumber(reading * scale, decimals);
          },
        });
      }

      if (withTotal && indices.length > 1) {
        columns.push({
          field: `${source.id}-${field}-total`,
          label: `${namePrefix}${this.translate.instant('devices.detailTabs.insights.total')} (${unit})`,
          weight: COLUMN_WEIGHT.electrical,
          value: interval => this.formatPhaseTotal(source.get(interval), field, scale, decimals),
        });
      }
    }

    return columns;
  }

  private formatPhaseTotal(
    emeter: EnergyFlowEmeterIntervalModel | undefined,
    field: keyof EnergyFlowEmeterIntervalModel,
    scale: number,
    decimals: number,
  ): string {
    const values = readings(emeter, field);
    if (!values) return '–';
    const present = values.filter((v): v is number => v != null);
    return present.length
      ? formatNumber(present.reduce((sum, v) => sum + v, 0) * scale, decimals)
      : '–';
  }

  // Grid connection first, then every labelled emeter channel that carries this metric.
  private meterSources(
    intervals: EnergyFlowIntervalModel[],
    field: keyof EnergyFlowEmeterIntervalModel,
  ): MeterSource[] {
    const sources: MeterSource[] = [];

    if (intervals.some(i => hasReadings(i.electricityGrid, field))) {
      sources.push({
        id: 'grid',
        label: this.translate.instant('devices.detailTabs.insights.grid.title'),
        get: i => i.electricityGrid,
      });
    }

    const channels = new Map<number, string>();
    for (const interval of intervals) {
      for (const emeter of interval.emeters ?? []) {
        if (hasReadings(emeter, field) && !channels.has(emeter.channelNumber)) {
          channels.set(emeter.channelNumber, emeter.label ?? `Emeter ${emeter.channelNumber}`);
        }
      }
    }
    for (const [channelNumber, label] of channels) {
      sources.push({
        id: `emeter-${channelNumber}`,
        label,
        get: i => i.emeters?.find(e => e.channelNumber === channelNumber),
      });
    }

    return sources;
  }

  // ---- Energy columns (grid + emeter consumed/delivered, per-device series) ----

  // Per-interval consumed/delivered plus the cumulative register readings, for the grid
  // connection and every emeter channel that carries energy readings.
  private meterEnergyColumns(intervals: EnergyFlowIntervalModel[]): DataColumn[] {
    const sources: MeterSource[] = [];

    if (intervals.some(i => i.electricityGrid && i.electricityGrid.quality !== 'missing')) {
      sources.push({
        id: 'grid',
        label: this.translate.instant('devices.detailTabs.insights.grid.title'),
        get: i => i.electricityGrid,
      });
    }
    const channels = new Map<number, string>();
    for (const interval of intervals) {
      for (const emeter of interval.emeters ?? []) {
        const hasEnergy =
          emeter.importedInWh != null ||
          emeter.exportedInWh != null ||
          emeter.importedInWhCumulativeValue != null ||
          emeter.exportedInWhCumulativeValue != null;
        if (hasEnergy && !channels.has(emeter.channelNumber)) {
          channels.set(emeter.channelNumber, emeter.label ?? `Emeter ${emeter.channelNumber}`);
        }
      }
    }
    for (const [channelNumber, label] of channels) {
      sources.push({
        id: `emeter-${channelNumber}`,
        label,
        get: i => i.emeters?.find(e => e.channelNumber === channelNumber),
      });
    }

    const columns: DataColumn[] = [];
    for (const source of sources) {
      for (const metric of EMETER_ENERGY_METRICS) {
        columns.push({
          field: `${source.id}-energy-${slug(metric.label)}`,
          label: `${source.label} ${this.translate.instant(metric.label).toLowerCase()} (${metric.unit})`,
          value: interval => {
            const emeter = source.get(interval);
            return emeter && emeter.quality !== 'missing'
              ? formatNumber(metric.raw(emeter), metric.decimals)
              : '–';
          },
        });
      }
    }
    return columns;
  }

  // Columns for a typed sub-device array (solar, pv forecast, battery, EV, heat pump),
  // one set of metric columns per channel. Multi-channel labels get a channel suffix.
  private deviceColumns<T extends { channelNumber: number; quality: string }>(
    intervals: EnergyFlowIntervalModel[],
    select: (interval: EnergyFlowIntervalModel) => T[] | undefined,
    metrics: MetricSpec<T>[],
    idPrefix: string,
  ): DataColumn[] {
    const channels = [
      ...new Set(intervals.flatMap(i => (select(i) ?? []).map(x => x.channelNumber))),
    ];
    const multi = channels.length > 1;
    const columns: DataColumn[] = [];

    for (const channel of channels) {
      const itemOf = (i: EnergyFlowIntervalModel) =>
        (select(i) ?? []).find(x => x.channelNumber === channel);

      for (const metric of metrics) {
        const base = this.translate.instant(metric.label);
        const named = multi ? `${base} ${channel}` : base;
        columns.push({
          field: `${idPrefix}-${channel}-${slug(metric.label)}`,
          label: metric.unit ? `${named} (${metric.unit})` : named,
          value: interval => {
            const item = itemOf(interval);
            return item && item.quality !== 'missing'
              ? formatNumber(metric.raw(item), metric.decimals)
              : '–';
          },
        });
      }
    }

    return columns;
  }
}
